import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import apiClient from '@/lib/api-client';
import {
    ArrowLeft, Plus, Save, Trash2, Gift, Package,
    DollarSign, Calculator, Sparkles, Tag,
    ChevronRight, ChevronLeft, CheckCircle, Info,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';

export function CreateHamperPage() {
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);

    const [currentStep, setCurrentStep] = useState(1);
    const [form, setForm] = useState({
        name: '', slug: '', description: '', sellingPrice: '', serviceCharge: '0',
        imageUrls: [] as string[],
        items: [] as { componentId: string; quantity: string; unitCost: string }[],
    });

    const { data: components } = useQuery({
        queryKey: ['hamper-components-create', user?.tenantId],
        queryFn: async () => {
            const { data } = await apiClient.get(`/tenants/${user!.tenantId}/hamper-components`);
            return data?.data || [];
        },
        enabled: !!user?.tenantId,
    });

    const createHamperMutation = useMutation({
        mutationFn: (payload: any) => apiClient.post(`/tenants/${user!.tenantId}/hampers`, payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['hampers'] });
            addToast({ type: 'success', title: 'Hamper created successfully' });
            navigate('/hampers');
        },
        onError: (err: any) => addToast({ type: 'error', title: 'Failed to create hamper', message: err?.response?.data?.error?.message }),
    });

    const addItem = () => {
        setForm({ ...form, items: [...form.items, { componentId: '', quantity: '1', unitCost: '' }] });
    };

    const removeItem = (idx: number) => {
        setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });
    };

    const updateItem = (idx: number, field: string, value: string) => {
        const updated = [...form.items];
        (updated[idx] as any)[field] = value;
        setForm({ ...form, items: updated });
    };

    const calculateTotalCost = () => {
        return form.items.reduce((sum, item) => {
            return sum + (parseFloat(item.quantity || '0') * parseFloat(item.unitCost || '0'));
        }, 0) + parseFloat(form.serviceCharge || '0');
    };

    const calculateProfit = () => {
        return parseFloat(form.sellingPrice || '0') - calculateTotalCost();
    };

    const calculateMargin = () => {
        const price = parseFloat(form.sellingPrice || '0');
        if (price <= 0) return 0;
        return ((calculateProfit()) / price) * 100;
    };

    const formatNaira = (val: string) => `₦${parseFloat(val || '0').toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

    const componentsList = (components || []) as any[];
    const canProceed = form.name && form.slug;
    const canSubmit = canProceed && form.sellingPrice && form.items.length > 0 && form.items.every((i: any) => i.componentId && i.quantity && i.unitCost);

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50/30">
            {/* Header */}
            <div className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-sm">
                <div className="max-w-4xl mx-auto px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button onClick={() => navigate('/hampers')} className="p-2 rounded-xl hover:bg-slate-100 transition-colors" type="button">
                                <ArrowLeft className="w-5 h-5 text-slate-600" />
                            </button>
                            <div className="p-2.5 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl shadow-lg shadow-amber-500/25">
                                <Gift className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h1 className="text-xl font-bold text-slate-900">Create Hamper</h1>
                                <p className="text-sm text-slate-500">Step {currentStep} of 2 · Bundle products into a gift package</p>
                            </div>
                        </div>
                        <button
                            onClick={() => createHamperMutation.mutate(form)}
                            disabled={!canSubmit || createHamperMutation.isPending}
                            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-xl hover:from-amber-600 hover:to-orange-700 transition-all font-semibold shadow-lg text-sm disabled:opacity-50"
                            type="button"
                        >
                            {createHamperMutation.isPending ? <Spinner size="xs" variant="white" /> : <Save className="w-4 h-4" />}
                            Create Hamper
                        </button>
                    </div>
                </div>
            </div>

            <div className="max-w-4xl mx-auto p-6 lg:p-8 space-y-6">
                {/* Step Indicator */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
                    <div className="flex items-center gap-2">
                        {[
                            { step: 1, title: 'Basic Details', desc: 'Name, pricing & description' },
                            { step: 2, title: 'Add Items', desc: `${form.items.length} component(s) selected` },
                        ].map((s, idx) => (
                            <div key={s.step} className="flex items-center gap-2 flex-1">
                                <button onClick={() => currentStep > s.step && setCurrentStep(s.step)}
                                    className={clsx(
                                        'flex items-center gap-3 px-4 py-3 rounded-xl transition-all flex-1',
                                        currentStep === s.step ? 'bg-amber-50 border-2 border-amber-300' :
                                            currentStep > s.step ? 'bg-emerald-50 border-2 border-emerald-200 cursor-pointer hover:bg-emerald-100' :
                                                'bg-slate-50 border-2 border-slate-200'
                                    )} type="button">
                                    <div className={clsx(
                                        'w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0',
                                        currentStep > s.step ? 'bg-emerald-500 text-white' :
                                            currentStep === s.step ? 'bg-amber-500 text-white' : 'bg-slate-300 text-slate-500'
                                    )}>
                                        {currentStep > s.step ? <CheckCircle size={16} /> : s.step}
                                    </div>
                                    <div className="text-left">
                                        <p className={clsx('text-sm font-semibold', currentStep >= s.step ? 'text-slate-800' : 'text-slate-400')}>{s.title}</p>
                                        <p className="text-[10px] text-slate-400">{s.desc}</p>
                                    </div>
                                </button>
                                {idx < 1 && <ChevronRight size={20} className="text-slate-300 flex-shrink-0" />}
                            </div>
                        ))}
                    </div>
                </div>

                {currentStep === 1 ? (
                    <Card>
                        <CardHeader gradient>
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-xl bg-gradient-to-br from-amber-100 to-orange-100">
                                    <Gift className="w-5 h-5 text-amber-600" />
                                </div>
                                <div>
                                    <h3 className="font-semibold text-slate-800">Hamper Details</h3>
                                    <p className="text-xs text-slate-500">Basic information about this gift bundle</p>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <Input
                                    label="Hamper Name *"
                                    value={form.name}
                                    onChange={(e) => setForm({
                                        ...form,
                                        name: e.target.value,
                                        slug: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
                                    })}
                                    placeholder="e.g., Valentine's Day Special"
                                    required
                                />
                                <Input
                                    label="Slug *"
                                    value={form.slug}
                                    onChange={(e) => setForm({ ...form, slug: e.target.value })}
                                    placeholder="valentines-day-special"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Description</label>
                                <textarea
                                    value={form.description}
                                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                                    rows={3}
                                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm resize-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-400"
                                    placeholder="Describe what's in this hamper and why it's special..."
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <Input
                                    label="Selling Price (₦) *"
                                    type="number"
                                    step="0.01"
                                    value={form.sellingPrice}
                                    onChange={(e) => setForm({ ...form, sellingPrice: e.target.value })}
                                    placeholder="0.00"
                                    required
                                />
                                <Input
                                    label="Service Charge (₦)"
                                    type="number"
                                    step="0.01"
                                    value={form.serviceCharge}
                                    onChange={(e) => setForm({ ...form, serviceCharge: e.target.value })}
                                    placeholder="0.00"
                                />
                            </div>
                            <div className="flex justify-end pt-2">
                                <button
                                    onClick={() => setCurrentStep(2)}
                                    disabled={!canProceed}
                                    className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-xl hover:from-amber-600 hover:to-orange-700 transition-all font-semibold text-sm shadow-lg disabled:opacity-50"
                                    type="button"
                                >
                                    Continue to Items <ChevronRight size={18} />
                                </button>
                            </div>
                        </CardContent>
                    </Card>
                ) : (
                    <>
                        <Card>
                            <CardHeader gradient>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 rounded-xl bg-gradient-to-br from-amber-100 to-orange-100">
                                            <Package className="w-5 h-5 text-amber-600" />
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-slate-800">Hamper Items</h3>
                                            <p className="text-xs text-slate-500">{form.items.length} component(s) · {form.items.filter((i: any) => i.componentId).length} configured</p>
                                        </div>
                                    </div>
                                    <button onClick={addItem}
                                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors"
                                        type="button">
                                        <Plus className="w-3.5 h-3.5" /> Add Component
                                    </button>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                {form.items.length === 0 ? (
                                    <div className="text-center py-8 text-slate-400">
                                        <Package size={40} className="mx-auto mb-3 opacity-50" />
                                        <p className="text-sm">No items added yet</p>
                                        <p className="text-xs mt-1">Click "Add Component" to start building your hamper</p>
                                    </div>
                                ) : (
                                    form.items.map((item, idx) => (
                                        <motion.div key={idx} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                                            className="p-4 bg-slate-50 rounded-2xl border border-slate-200">
                                            <div className="grid grid-cols-12 gap-3 items-end">
                                                <div className="col-span-5">
                                                    <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1 block">Component *</label>
                                                    <select
                                                        value={item.componentId}
                                                        onChange={(e) => {
                                                            const comp = componentsList.find((c: any) => c.id === e.target.value);
                                                            updateItem(idx, 'componentId', e.target.value);
                                                            if (comp) updateItem(idx, 'unitCost', comp.costPrice);
                                                        }}
                                                        className="w-full px-3 py-2.5 border-2 border-slate-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-amber-500/20 focus:border-amber-400"
                                                        required
                                                    >
                                                        <option value="">Select component...</option>
                                                        {componentsList.map((c: any) => (
                                                            <option key={c.id} value={c.id}>
                                                                {c.name} ({c.type?.replace(/_/g, ' ')})
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="col-span-2">
                                                    <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1 block">Quantity</label>
                                                    <input
                                                        type="number" step="0.01" min="0.01"
                                                        value={item.quantity}
                                                        onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
                                                        className="w-full px-2 py-2.5 border-2 border-slate-200 rounded-lg text-sm text-right"
                                                        placeholder="1"
                                                    />
                                                </div>
                                                <div className="col-span-2">
                                                    <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1 block">Unit Cost (₦)</label>
                                                    <input
                                                        type="number" step="0.01" min="0"
                                                        value={item.unitCost}
                                                        onChange={(e) => updateItem(idx, 'unitCost', e.target.value)}
                                                        className="w-full px-2 py-2.5 border-2 border-slate-200 rounded-lg text-sm text-right"
                                                        placeholder="0.00"
                                                    />
                                                </div>
                                                <div className="col-span-2">
                                                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Line Total</p>
                                                    <p className="text-sm font-bold text-slate-800 mt-1">
                                                        {formatNaira(String(parseFloat(item.quantity || '0') * parseFloat(item.unitCost || '0')))}
                                                    </p>
                                                </div>
                                                <div className="col-span-1 flex items-end pb-2">
                                                    <button type="button" onClick={() => removeItem(idx)}
                                                        className="p-2 text-rose-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors w-full flex justify-center"
                                                        title="Remove item">
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </div>
                                        </motion.div>
                                    ))
                                )}
                            </CardContent>
                        </Card>

                        {/* Pricing Summary */}
                        <Card>
                            <CardHeader gradient>
                                <div className="flex items-center gap-3">
                                    <div className="p-2 rounded-xl bg-gradient-to-br from-emerald-100 to-teal-100">
                                        <Calculator className="w-5 h-5 text-emerald-600" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-slate-800">Pricing Summary</h3>
                                        <p className="text-xs text-slate-500">Review costs and profit</p>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-2">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-slate-500">Total Component Cost</span>
                                        <span className="font-semibold">{formatNaira(String(calculateTotalCost() - parseFloat(form.serviceCharge || '0')))}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-slate-500">Service Charge</span>
                                        <span>{formatNaira(form.serviceCharge || '0')}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-slate-500">Total Cost</span>
                                        <span className="font-semibold">{formatNaira(String(calculateTotalCost()))}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-slate-500">Selling Price</span>
                                        <span className="text-emerald-600 font-semibold">{formatNaira(form.sellingPrice)}</span>
                                    </div>
                                    <div className="flex justify-between text-base font-bold pt-2 border-t border-slate-200">
                                        <span>Profit</span>
                                        <span className={clsx(calculateProfit() >= 0 ? 'text-emerald-600' : 'text-rose-600')}>
                                            {formatNaira(String(calculateProfit()))}
                                            <span className="text-xs ml-1">({calculateMargin().toFixed(0)}%)</span>
                                        </span>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Navigation */}
                        <div className="flex items-center justify-between">
                            <button onClick={() => setCurrentStep(1)}
                                className="flex items-center gap-2 px-5 py-3 border-2 border-slate-300 rounded-xl hover:bg-white transition font-medium text-sm"
                                type="button">
                                <ChevronLeft size={18} /> Back to Details
                            </button>
                            <div className="flex items-center gap-3">
                                <button onClick={() => navigate('/hampers')}
                                    className="px-5 py-3 border-2 border-slate-300 rounded-xl hover:bg-white transition font-medium text-sm"
                                    type="button">
                                    Cancel
                                </button>
                                <button
                                    onClick={() => createHamperMutation.mutate(form)}
                                    disabled={!canSubmit || createHamperMutation.isPending}
                                    className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-xl hover:from-amber-600 hover:to-orange-700 transition-all font-bold text-sm shadow-xl shadow-amber-500/25 disabled:opacity-50"
                                    type="button"
                                >
                                    {createHamperMutation.isPending ? <Spinner size="xs" variant="white" /> : <Save size={18} />}
                                    Create Hamper
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}